import { DROPZONES_NAMES } from 'pages/DesignOrchestration/DesignOrchestrationDiagram/DesignOrchestrationDiagram.constans';
import { mapOrchestrationStep } from 'dto/orchestrationDiagram/orchestrationSteps';
import { dropZoneStepTypes } from 'interfaces/designOrchestration/designOrchestrationDiagram.interface';
import { IServerWorkflow } from 'interfaces/workflow.interface';
import { STEP_STATUSES } from 'utils/common-constants';
import { DateUtils } from 'utils/dateUtils/DateUtils';
import { Useuid } from 'utils/hooks/useUid';
import {
  IOrchestrationFullStep,
  IOrchestrationServer,
  IOrchestrationStepGroup,
  IStepInformation,
} from 'interfaces/orchestration.interface';
import {
  IEdges,
  IOrchestrationNode,
  IOrchestrationSidePanelSteps,
  stepTypes,
} from 'interfaces/orchestrationDiagram/orchestrationDiagram.interface';
import { IDropZoneWorkflowState } from 'interfaces/designOrchestration/designOrchestrationState.interface';
import {
  normalizeDate,
  getDateDifference,
} from '../../pages/OrchestrationDetails/hooks/timeHelpers';

let X_GAP = 50;
let Y_GAP = 30;
let NODE_WIDTH = 300 + X_GAP;
const NODE_HEIGHT = 80;

const getNestGroupId = (nestGroupId: any, step: any) => {
  const existingNestGroupId = Array.isArray(step.nestGroupId)
    ? step.nestGroupId
    : [];

  const newNestGroupId = [...existingNestGroupId];

  if (nestGroupId) {
    if (Array.isArray(nestGroupId)) {
      nestGroupId.forEach((id: string) => {
        if (id !== undefined && id !== null && !newNestGroupId.includes(id)) {
          newNestGroupId.push(id);
        }
      });
    } else if (
      nestGroupId !== undefined &&
      nestGroupId !== null &&
      !newNestGroupId.includes(nestGroupId)
    ) {
      newNestGroupId.push(nestGroupId);
    }
  }
  return newNestGroupId;
};

//get initial steps and tag them with necesary properties
export const getSteps = (
  stepsGroup: any,
  acc: IOrchestrationFullStep[][],
  isNestedStep: boolean,
  stepType: stepTypes | dropZoneStepTypes,
  nestGroupId?: any
): IOrchestrationFullStep[][] => {
  if (stepsGroup) {
    const newStep = stepsGroup.steps?.map((step: any) => {
      const newNestGroupId = getNestGroupId(nestGroupId, step);
      return {
        ...step,
        isNestedStep,
        stepType: step.stepType || stepType,
        nestGroupId: newNestGroupId,
      };
    });
    acc.push(newStep);
    getNestedAndFailbackSteps(newStep, acc, isNestedStep, stepType);
    getSteps(
      stepsGroup.nextStepGroup,
      acc,
      isNestedStep,
      stepType,
      nestGroupId
    );
  }
  return acc;
};

const getNestedAndFailbackSteps = (
  newStep: any,
  acc: IOrchestrationFullStep[][],
  isNestedStep: boolean,
  stepType: stepTypes | dropZoneStepTypes
) => {
  newStep?.forEach((step: any) => {
    if (!step.nestGroupId) {
      step.nestGroupId = [];
    } else if (!Array.isArray(step.nestGroupId)) {
      step.nestGroupId = [step.nestGroupId].filter(Boolean);
    }
    const nestedGroupId = [...step.nestGroupId];
    const stepId = step.workflowStepId?.toString();
    if (stepId && !nestedGroupId.includes(stepId)) {
      nestedGroupId.push(stepId);
    }

    if (step.failbackStepGroup) {
      getSteps(
        step.failbackStepGroup,
        acc,
        stepType === 'globalFailback' ? isNestedStep : true,
        step.failbackStepGroup.steps[0]?.stepType ||
          (stepType === 'globalFailback' ? stepType : 'failback'),
        nestedGroupId
      );
    }
    if (step.nestedStepGroup) {
      getSteps(
        step.nestedStepGroup,
        acc,
        true,
        step.nestedStepGroup.steps[0]?.stepType || stepType,
        nestedGroupId
      );
    }
  });
};

export const getStepsWithGroups = (workflow: any) => {
  const result: any = [];

  // Helper function to recursively traverse the steps and groups
  const traverseSteps = (stepGroup: any) => {
    if (!stepGroup) {
      return;
    }

    for (const step of stepGroup.steps) {
      const stepInfo = {
        stepTitle: step.stepTitle,
        workflowStepId: step.workflowStepId,
        stepIndex: step.stepIndex,
        workflowStepGroupId: step.workflowStepGroupId,
        childs: [
          step.nestedStepGroup?.steps?.map((x: any) => ({
            workflowStepId: x.workflowStepId,
            stepIndex: x.stepIndex,
            stepTitle: x.stepTitle,
            workflowStepGroupId: x.workflowStepGroupId,
          })),
          stepGroup.nextStepGroup?.steps?.map((x: any) => ({
            workflowStepId: x.workflowStepId,
            stepIndex: x.stepIndex,
            stepTitle: x.stepTitle,
            workflowStepGroupId: x.workflowStepGroupId,
          })),
          step.failbackStepGroup?.steps?.map((x: any) => ({
            workflowStepId: x.workflowStepId,
            stepIndex: x.stepIndex,
            stepTitle: x.stepTitle,
            workflowStepGroupId: x.workflowStepGroupId,
          })),
        ]
          .filter((x) => x !== undefined)
          .flat(),
      };
      result.push(stepInfo);

      // Recursively traverse nested step groups
      traverseSteps(step.nestedStepGroup);

      // Recursively traverse failback step groups
      traverseSteps(step.failbackStepGroup);
    }

    // Recursively traverse next step groups
    traverseSteps(stepGroup.nextStepGroup);
  };

  traverseSteps(workflow.nextStepGroup);

  return result;
};

export function processOrchestration(
  orchestration:
    | Partial<IOrchestrationServer>
    | Partial<IServerWorkflow>
    | IDropZoneWorkflowState,
  containerWidth = 1000,
  steps: IOrchestrationFullStep[][],
  isOnDesignOrchestration = false
): {
  nodes: IOrchestrationNode[];
  orchestrarionSteps: IOrchestrationSidePanelSteps[];
} {
  if (!steps || !steps.length) {
    return { nodes: [], orchestrarionSteps: [] };
  }
  let stepsParamsData: any = undefined;

  if ('steps' in orchestration) {
    stepsParamsData = getStepsParamsData(orchestration.steps);
  }
  const stepsSideBarData = generateStepsSideBarData(steps, stepsParamsData);

  const orchestrationContext = {
    currentY: 0,
    nodeTracker: {},
    processedNodes: [],
    stepsParamsData,
  };

  if (isOnDesignOrchestration === true) {
    Y_GAP = 3;
    X_GAP = 20;
    NODE_WIDTH = 241 + X_GAP;
  } else {
    Y_GAP = 30;
    X_GAP = 50;
    NODE_WIDTH = 300 + X_GAP;
  }

  processWorkflowSteps(
    orchestration,
    orchestrationContext,
    containerWidth,
    isOnDesignOrchestration
  );
  return {
    nodes: orchestrationContext.processedNodes,
    orchestrarionSteps: stepsSideBarData,
  };
}

function processWorkflowSteps(
  orchestration: Partial<IOrchestrationServer> | Partial<IServerWorkflow> | any,
  context: any,
  containerWidth: number,
  isOnDesignOrchestration = false
) {
  const ORCHESTRATION_DETAILS_X_OFFSET = 150;
  const DESIGN_ORCHESTRATION_X_OFFSET = 0;
  const DESIGN_ORCHESTRATION_Y_OFFSET = 80;
  const { processIndividualStepGroup } =
    initializeStepProcessingFunctions(context);

  if (orchestration.workflow?.nextStepGroup || orchestration.nextStepGroup) {
    const workflow =
      orchestration.workflow?.nextStepGroup ?? orchestration.nextStepGroup;

    processIndividualStepGroup(
      workflow,
      containerWidth / 2 -
        (isOnDesignOrchestration === true
          ? DESIGN_ORCHESTRATION_X_OFFSET
          : ORCHESTRATION_DETAILS_X_OFFSET),
      isOnDesignOrchestration === true ? DESIGN_ORCHESTRATION_Y_OFFSET : 0,
      'step'
    );
    context.currentY += NODE_HEIGHT + Y_GAP;
  }
  if (
    orchestration.workflow?.failbackStepGroup ||
    orchestration.failbackStepGroup
  ) {
    const workflow =
      orchestration.workflow?.failbackStepGroup ??
      orchestration.failbackStepGroup;
    processIndividualStepGroup(
      workflow,
      containerWidth / 2 -
        (isOnDesignOrchestration === true
          ? DESIGN_ORCHESTRATION_X_OFFSET
          : ORCHESTRATION_DETAILS_X_OFFSET),
      context.currentY + NODE_HEIGHT + Y_GAP,
      'globalFailback'
    );
  }
}

function initializeStepProcessingFunctions(context: any) {
  function calculateGroupWidth(stepGroup: any): number {
    if (!stepGroup || !stepGroup.steps) {
      return 0;
    }
    let totalWidth = 0;
    let maxWidthNestedFailback = 0;
    const nextGroupWidth = stepGroup.nextStepGroup
      ? calculateGroupWidth(stepGroup.nextStepGroup)
      : 0;
    for (const step of stepGroup.steps) {
      const nestedWidth = step.nestedStepGroup
        ? calculateGroupWidth(step.nestedStepGroup)
        : 0;
      const failbackWidth = step.failbackStepGroup
        ? calculateGroupWidth(step.failbackStepGroup)
        : 0;

      // Add current step's width to total width of the group
      totalWidth += Math.max(nestedWidth, failbackWidth, NODE_WIDTH);

      maxWidthNestedFailback = Math.max(
        maxWidthNestedFailback,
        nestedWidth,
        failbackWidth,
        nextGroupWidth
      );
    }

    return Math.max(totalWidth, nextGroupWidth);
  }

  function processIndividualStepGroup(
    stepGroup: IOrchestrationStepGroup,
    startX: number,
    startY: number,
    stepType: stepTypes
  ) {
    if (!stepGroup || !stepGroup.steps) {
      return { maxX: startX, maxY: startY };
    }

    context.currentY = Math.max(context.currentY, startY);

    let totalChildrenWidth = 0;

    if (stepGroup.steps?.length > 1) {
      for (const step of stepGroup.steps) {
        if (step.nestedStepGroup || step.failbackStepGroup) {
          totalChildrenWidth += calculateGroupWidth({ steps: [step] });
        } else {
          totalChildrenWidth += NODE_WIDTH;
        }
      }
    }

    let { maxXPosition, maxYPosition } = processAndPositionSteps(
      stepGroup,
      startX,
      startY,
      stepType,
      totalChildrenWidth
    );

    if (stepGroup.nextStepGroup) {
      const { maxX: nextGroupX, maxY: nextGroupY } = processIndividualStepGroup(
        stepGroup.nextStepGroup,
        maxXPosition,
        maxYPosition + NODE_HEIGHT + Y_GAP,
        stepType
      );
      maxXPosition = Math.max(maxXPosition, nextGroupX);
      maxYPosition = Math.max(maxYPosition, nextGroupY);
    }

    return { maxX: maxXPosition, maxY: maxYPosition };
  }

  function createNodeData(
    step: IOrchestrationFullStep,
    x: number,
    y: number,
    stepParamsData: any,
    stepType: stepTypes | dropZoneStepTypes
  ) {
    const tooltip = step.stepIndex
      ? undefined
      : generateTooltip(step, stepType);

    const nodeData: any = {
      name: step.stepTitle || '',
      id: step.workflowStepId?.toString(),
      moduleName: step.module?.name || '',
      description: step.module?.description || '',
      duration: getNodeDuration(stepParamsData),
      status: stepParamsData?.status || 'NOT_STARTED',
      index: step.stepIndex,
      stepType: step.stepType || stepType,
      version: step.module?.version || 'N/A',
      isConditional: !!step.stepCondition,
      ...(tooltip !== undefined && { tooltip }),
      ...(step.associatedMainNodeId && {
        associatedMainNodeId: step.associatedMainNodeId,
      }),
    };

    return {
      id: step.workflowStepId?.toString() || Useuid(),
      type:
        step.stepTitle &&
        Object.values(DROPZONES_NAMES).includes(step.stepTitle as any)
          ? 'dropZoneNode'
          : 'orchestrationFlowElement',
      data: nodeData,
      position: { x, y },
    };
  }

  const processAndPositionSteps = (
    stepGroup: IOrchestrationStepGroup,
    currentX: number,
    startY: number,
    stepType: stepTypes,
    totalChildrenWidth: number
  ): any => {
    const maxXPosition = currentX;
    let maxYPosition = startY;

    let previousY;
    for (const step of stepGroup.steps) {
      if (context.nodeTracker[step.workflowStepId]) {
        continue; // Skip already processed nodes
      }

      const stepWidth = calculateGroupWidth({ steps: [step] });
      previousY = startY; // Store the parent step's Y position for this iteration

      // Calculate offset for centering the step and its potential children
      const stepX = calculateStepXPosition(
        totalChildrenWidth,
        stepGroup.steps.length,
        stepWidth,
        currentX
      );

      // Calculate the common starting Y for both failback and nested groups
      const subgroupStartY = previousY + NODE_HEIGHT + Y_GAP;
      let failbackMaxY = previousY; // Initialize max Y for failback path
      let nestedMaxY = previousY; // Initialize max Y for nested path

      // Process failback group if it exists
      if (step.failbackStepGroup) {
        const { maxY } = processIndividualStepGroup(
          step.failbackStepGroup,
          stepX,
          subgroupStartY, // Start below the parent
          'failback'
        );
        failbackMaxY = maxY; // Store the max Y reached by the failback group
      }

      // Process nested group if it exists
      if (step.nestedStepGroup) {
        const nestedStartY = step.failbackStepGroup
          ? failbackMaxY + NODE_HEIGHT + Y_GAP
          : subgroupStartY;
        const { maxY } = processIndividualStepGroup(
          step.nestedStepGroup,
          stepX,
          nestedStartY, // Start below the parent, at the same Y as failback
          'step'
        );
        nestedMaxY = maxY; // Store the max Y reached by the nested group
      }

      // Update the overall maxYPosition based on the maximum Y reached by *either* subgroup
      maxYPosition = Math.max(maxYPosition, failbackMaxY, nestedMaxY);

      // Create and push node data for the PARENT step
      const stepParamsData = context.stepsParamsData?.[step.workflowStepId];
      context.processedNodes.push(
        createNodeData(step, stepX, startY, stepParamsData, stepType)
      );

      // Mark the step as processed
      context.nodeTracker[step.workflowStepId] = true;
      if (stepGroup.steps?.length > 1) {
        currentX += stepWidth;
      } else {
        currentX = maxXPosition; // This ensures steps are placed correctly after nested or failback groups
      }
    }

    return { maxXPosition, maxYPosition };
  };

  // Helper function to calculate the X position of a step
  function calculateStepXPosition(
    totalChildrenWidth: number,
    stepsLength: number,
    stepWidth: number,
    currentX: number
  ): number {
    let offset = 0;
    if (totalChildrenWidth > NODE_WIDTH && stepsLength > 1) {
      offset = (stepWidth - totalChildrenWidth) / 2;
    }
    return offset + currentX;
  }

  return { processIndividualStepGroup };
}

export const generateTooltip = (
  step: any,
  stepType: stepTypes | dropZoneStepTypes
) => {
  if (step.stepIndex !== null) {
    return undefined;
  }

  if ((step.stepType as dropZoneStepTypes) === 'nested') {
    return 'Drag and drop your module here to add a nested step';
  }

  if ((step.stepType as dropZoneStepTypes) === 'nestedFailback') {
    return "Drag and drop your failback module here. Modules in the 'Nested failback drop zone' are executed if the parent step fails";
  }

  if ((stepType as stepTypes) === 'step') {
    if (step.stepTitle === DROPZONES_NAMES.PARALLEL) {
      return 'Drag and drop your module here to add a parallel step to your orchestration';
    }
    return 'Drag and drop your module here to add a step to your orchestration';
  }

  if ((stepType as stepTypes) === 'failback' || stepType === 'globalFailback') {
    if (step.stepTitle === DROPZONES_NAMES.PARALLEL) {
      return 'Drag and drop your failback module here to add a parallel step to your orchestration';
    } else {
      return 'Drag and drop your failback module here to add a step to your orchestration';
    }
  }

  return undefined;
};

const getNodeDuration = (stepParamsData: any) => {
  return stepParamsData?.status === STEP_STATUSES.NOT_STARTED
    ? '-'
    : getDateDifference(
        normalizeDate(stepParamsData?.startTime, true),
        normalizeDate(stepParamsData?.endTime || new Date().toISOString(), true)
      );
};

export const getStepsParamsData = (
  steps: IStepInformation[] | undefined
): { [key: number]: IStepInformation } | undefined => {
  if (steps === undefined || steps === null) {
    return undefined;
  }
  const stepsObject: { [key: number]: IStepInformation } = {};
  steps.forEach((step: any) => {
    stepsObject[step.workflowStepId] = step;
  });
  return stepsObject;
};

export const generateStepsSideBarData = (
  steps: IOrchestrationFullStep[][],
  stepsObject: any[] | undefined
): any[] => {
  const stepsSideBarData: any = [];
  steps?.forEach((stepGroup: IOrchestrationFullStep[]) =>
    stepGroup?.forEach((step: IOrchestrationFullStep) =>
      stepsSideBarData.push(
        mapOrchestrationStep(step, stepsObject?.[step.workflowStepId])
      )
    )
  );
  return stepsSideBarData;
};

export const generateEdges = (
  stepGroups: IOrchestrationFullStep[][]
): IEdges[] => {
  const edges: IEdges[][] = [];
  stepGroups?.forEach((group, index) => {
    group?.forEach((currentStep: IOrchestrationFullStep) => {
      if (!currentStep) {
        return;
      }
      const nextFailBackGroup = currentStep.failbackStepGroup?.steps;
      const nextNestedGroup = currentStep.nestedStepGroup?.steps;
      const nextGroup = findNextGroup(stepGroups, index, currentStep);

      if (nextFailBackGroup?.length) {
        edges.push(setEdgesBasedOnStepGroup(nextFailBackGroup, currentStep));
      } else if (nextNestedGroup?.length) {
        edges.push(setEdgesBasedOnStepGroup(nextNestedGroup, currentStep));
      } else if (nextGroup?.length) {
        edges.push(setEdgesBasedOnStepGroup(nextGroup, currentStep));
      }
    });
  });
  return edges.flat();
};

const findNextGroup = (
  stepGroups: IOrchestrationFullStep[][],
  index: number,
  currentStep: IOrchestrationFullStep
): IOrchestrationFullStep[] | undefined => {
  return stepGroups.slice(index + 1).find((steps) => {
    return steps?.some((step) => {
      const nestGroupIds = [...(step.nestGroupId || [])].reverse();
      return (
        (!step.isNestedStep && nestGroupIds[0] === undefined) ||
        (currentStep.nestGroupId?.includes(nestGroupIds[0]) &&
          currentStep.workflowStepId > step.workflowStepId &&
          compareStepIndices(currentStep, step))
      );
    });
  });
};

export function setEdgesBasedOnStepGroup(
  stepsGroup: IOrchestrationFullStep[],
  currentStep: IOrchestrationFullStep
): IEdges[] {
  return stepsGroup
    .map((step: IOrchestrationFullStep) => {
      if (
        currentStep.workflowStepId?.toString() !==
        step.workflowStepId?.toString()
      ) {
        return {
          id: `edge-${Useuid()}`,
          source: currentStep.workflowStepId?.toString(),
          target: step.workflowStepId?.toString(),
          sourceHandle: 'bottom',
          type: 'smartEdges',
          className: 'csb-custom-edge',
        } as IEdges;
      } else {
        return null;
      }
    })
    .filter((edge): edge is IEdges => edge !== null);
}

function compareStepIndices(
  currentStep: IOrchestrationFullStep,
  step: IOrchestrationFullStep
): boolean {
  const currentStepIndex = currentStep.stepIndex;
  const stepIndex = step.stepIndex;

  if (currentStepIndex.includes('E') && !stepIndex.includes('E')) {
    return true;
  }

  if (!currentStepIndex.includes('E') && stepIndex.includes('E')) {
    return true;
  }

  const currentStepIndexNumber = currentStepIndex.replace('E', '');
  const stepIndexNumber = stepIndex.replace('E', '');

  if (
    !isNaN(Number(currentStepIndexNumber)) &&
    !isNaN(Number(stepIndexNumber))
  ) {
    return Number(currentStepIndexNumber) < Number(stepIndexNumber);
  } else {
    return true;
  }
}
